import { API_URL } from "../../api/apiConfig";
import {
  fileWithMeetingPhotoName,
  meetingPhotoDownloadName,
} from "./meetingLinkedSampleLabel";

function mediaUrl(fileUrl) {
  if (!fileUrl) return "";
  if (/^(https?:|blob:|data:)/i.test(fileUrl)) return fileUrl;
  const base = String(API_URL || "").replace(/\/$/, "");
  return `${base}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
}

function asImageFile(blob, name) {
  if (!blob || blob.size < 32) return null;
  let type = String(blob.type || "").toLowerCase();
  if (
    type.includes("html") ||
    type.includes("json") ||
    type.includes("text/plain")
  ) {
    return null;
  }
  if (!type.startsWith("image/")) {
    if (/\.png$/i.test(name)) type = "image/png";
    else if (/\.webp$/i.test(name)) type = "image/webp";
    else if (/\.gif$/i.test(name)) type = "image/gif";
    else type = "image/jpeg";
  }
  return new File([blob], name, { type });
}

const photoFileCache = new Map();
const photoDataUrlCache = new Map();

/** Chrome corta setData si el data: es el jpeg entero; Unibox no ve el File. */
export const MAX_DRAG_DATA_URL_CHARS = 500000;
export const CRM_PHOTO_FILENAME_TYPE = "application/x-crm-photo-filename";

export function cachedMeetingPhotoFile(photo) {
  return photoFileCache.get(Number(photo?.id)) || null;
}

export function cachedMeetingPhotoDataUrl(photo) {
  return photoDataUrlCache.get(Number(photo?.id)) || "";
}

export function invalidateMeetingPhotoCache(photoId) {
  const id = Number(photoId);
  if (!Number.isFinite(id)) return;
  photoFileCache.delete(id);
  photoDataUrlCache.delete(id);
}

export function meetingPhotoFetchUrls(photo) {
  if (!photo) return [];
  const urls = [];
  for (const raw of [
    photo.displayFileUrl,
    photo.fileUrl,
    photo.muestraFrontUrl,
  ]) {
    const url = mediaUrl(raw) || "";
    if (url && !urls.includes(url)) urls.push(url);
  }
  const display = mediaUrl(
    photo.displayFileUrl || photo.muestraFrontUrl || photo.fileUrl || "",
  );
  if (display && !urls.includes(display)) urls.unshift(display);
  return urls;
}

async function blobFromUrl(url) {
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";
  // Auth primero: sin token el back suele devolver HTML/401 y el canvas no vale.
  const attempts = [];
  if (token) {
    attempts.push({
      mode: "cors",
      headers: { Authorization: `Bearer ${token}` },
      cache: "force-cache",
    });
  }
  attempts.push({ mode: "cors", cache: "force-cache" });
  for (const init of attempts) {
    try {
      const res = await fetch(url, init);
      if (!res.ok) continue;
      const blob = await res.blob();
      const type = String(blob.type || "").toLowerCase();
      if (
        type.includes("html") ||
        type.includes("json") ||
        type.includes("text/plain")
      ) {
        continue;
      }
      if (blob.size < 32) continue;
      return blob;
    } catch {
      /* CORS / red */
    }
  }
  return null;
}

function canvasJpegDataUrl(img, width, height, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

export function compactJpegDataUrlFromImage(img) {
  if (!img || !img.naturalWidth) return "";
  try {
    let width = img.naturalWidth;
    let height = img.naturalHeight;
    const maxEdge = 1280;
    if (Math.max(width, height) > maxEdge) {
      const scale = maxEdge / Math.max(width, height);
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));
    }
    let quality = 0.78;
    let dataUrl = canvasJpegDataUrl(img, width, height, quality);
    while (dataUrl.length > MAX_DRAG_DATA_URL_CHARS && quality > 0.45) {
      quality -= 0.12;
      dataUrl = canvasJpegDataUrl(img, width, height, quality);
    }
    while (
      dataUrl.length > MAX_DRAG_DATA_URL_CHARS &&
      Math.max(width, height) > 360
    ) {
      width = Math.max(1, Math.round(width * 0.7));
      height = Math.max(1, Math.round(height * 0.7));
      dataUrl = canvasJpegDataUrl(img, width, height, 0.7);
    }
    if (
      !dataUrl.startsWith("data:image/") ||
      dataUrl.length > MAX_DRAG_DATA_URL_CHARS
    ) {
      return "";
    }
    return dataUrl;
  } catch {
    return "";
  }
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error("timeout")), 4000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("image"));
    };
    img.src = url;
  });
}

async function compactFileAsDataUrl(file) {
  if (!file) return "";
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageFromUrl(objectUrl);
    return compactJpegDataUrlFromImage(img);
  } catch {
    return "";
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** data: a tamaño completo si cabe en DownloadURL; si no, jpeg compacto. */
async function dragDataUrlFromFile(file) {
  if (!file) return "";
  const maxBytes = Math.floor((MAX_DRAG_DATA_URL_CHARS * 3) / 4) - 64;
  if (file.size > 0 && file.size <= maxBytes) {
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const mime =
        String(file.type || "image/jpeg")
          .split(";")[0]
          .trim() || "image/jpeg";
      const dataUrl = `data:${mime};base64,${btoa(binary)}`;
      if (
        dataUrl.startsWith("data:image/") &&
        dataUrl.length <= MAX_DRAG_DATA_URL_CHARS
      ) {
        return dataUrl;
      }
    } catch {
      /* fallback compacto */
    }
  }
  return compactFileAsDataUrl(file);
}

async function rememberPhotoFile(photo, file) {
  const named = fileWithMeetingPhotoName(photo, file);
  const key = Number(photo?.id);
  if (named && key) {
    photoFileCache.set(key, named);
    if (!photoDataUrlCache.get(key)) {
      const dataUrl = await dragDataUrlFromFile(named);
      if (dataUrl) photoDataUrlCache.set(key, dataUrl);
    }
  }
  return named;
}

export async function fetchMeetingPhotoAsFile(photo) {
  if (!photo) return null;
  const cached = cachedMeetingPhotoFile(photo);
  if (cached) return rememberPhotoFile(photo, cached);
  for (const url of meetingPhotoFetchUrls(photo)) {
    const blob = await blobFromUrl(url);
    if (!blob) continue;
    const name = meetingPhotoDownloadName(photo, blob.type || photo.mimeType);
    const file = asImageFile(blob, name);
    if (!file) continue;
    return rememberPhotoFile(photo, file);
  }
  return null;
}

/**
 * No usar blob: en DownloadURL: Chrome lo descarga al soltar y falla si el
 * blob ya no existe. data: es lo que el download manager acepta (Explorador).
 */
export function revokeMeetingDragBlobUrls() {
  /* compat: ya no creamos blob: para drag; noop */
}

/** PNG en portapapeles — Illustrator no acepta el arrastre async de Chrome. */
export async function rasterFileToPngBlob(file) {
  if (!file?.size) return null;
  if (String(file.type || "").toLowerCase() === "image/png") return file;
  try {
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        bitmap.close?.();
        return null;
      }
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close?.();
      const png = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/png");
      });
      return png && png.size > 32 ? png : null;
    }
  } catch {
    /* fallback FileReader + Image */
  }
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("read"));
      reader.readAsDataURL(file);
    });
    const img = await loadImageFromUrl(dataUrl);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const png = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
    });
    return png && png.size > 32 ? png : null;
  } catch {
    return null;
  }
}

/**
 * Chrome→Illustrator no puede entregar CF_HDROP (solo Explorador hace el
 * download async). Pegar PNG del portapapeles sí funciona en AI.
 * ClipboardItem con Promise conserva el user-gesture del dragstart.
 */
export async function copyMeetingPhotoFileToClipboard(file) {
  if (!file?.size) return false;
  if (typeof ClipboardItem === "undefined") return false;
  if (!navigator?.clipboard?.write) return false;
  try {
    const pngPromise = Promise.resolve(rasterFileToPngBlob(file)).then(
      (png) => {
        if (!png) throw new Error("png");
        return png;
      },
    );
    await navigator.clipboard.write([
      new ClipboardItem({ "image/png": pngPromise }),
    ]);
    return true;
  } catch {
    /* probar mime original */
  }
  try {
    const type =
      String(file.type || "image/jpeg").split(";")[0] || "image/jpeg";
    await navigator.clipboard.write([new ClipboardItem({ [type]: file })]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Escritorio: File + DownloadURL data: (Chrome materializa en carpetas).
 * Illustrator: no usa ese camino — hace falta portapapeles (Ctrl+V).
 *   — No text/plain ni text/uri-list.
 */
export function attachNamedFileToDrag(event, file, dataUrl, options = {}) {
  if (!event?.dataTransfer || !file || !file.size) return false;
  const dt = event.dataTransfer;
  dt.effectAllowed = "copy";
  const downloadUrl = options.downloadUrl !== false;
  try {
    dt.clearData();
  } catch {
    /* ignore */
  }

  const name = String(file.name || "foto.jpg").replace(/:/g, "-");
  const mime =
    String(file.type || "image/jpeg")
      .split(";")[0]
      .trim() || "image/jpeg";
  let ok = false;

  try {
    dt.items.add(file);
    ok = true;
  } catch {
    /* Unibox / algunos hosts solo leen DownloadURL */
  }

  if (downloadUrl) {
    const inline = String(dataUrl || "");
    if (
      inline.startsWith("data:image/") &&
      inline.length <= MAX_DRAG_DATA_URL_CHARS
    ) {
      try {
        // MIME:filename:URL — el resto tras el 2º ':' es la URL (data: tiene ':')
        const downloadMime = inline.startsWith("data:image/png")
          ? "image/png"
          : inline.startsWith("data:image/webp")
            ? "image/webp"
            : "image/jpeg";
        const downloadName = /\.(jpe?g|png|webp|gif)$/i.test(name)
          ? name.replace(/\.(png|webp|gif)$/i, ".jpg")
          : `${name}.jpg`;
        dt.setData("DownloadURL", `${downloadMime}:${downloadName}:${inline}`);
        ok = true;
      } catch {
        /* DLP corporativo a veces bloquea DownloadURL */
      }
    }
  }

  try {
    dt.setData(CRM_PHOTO_FILENAME_TYPE, name);
  } catch {
    /* ignore */
  }
  return ok;
}

export function fileFromLoadedImage(img, fileName) {
  if (!img || !img.naturalWidth) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const comma = dataUrl.indexOf(",");
    if (comma < 0) return null;
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    let name = String(fileName || "foto.jpg").replace(/:/g, "-");
    if (!/\.(jpe?g|png|webp|gif)$/i.test(name)) name = `${name}.jpg`;
    else name = name.replace(/\.png$/i, ".jpg");
    return new File([bytes], name, { type: "image/jpeg" });
  } catch {
    return null;
  }
}

export function isSafariBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const vendor = navigator.vendor || "";
  return (
    /Apple/i.test(vendor) &&
    /Safari/i.test(ua) &&
    !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS/i.test(ua)
  );
}

export function startMeetingPhotoDrag(event, photo, file, options = {}) {
  const cancelIfEmpty = options.cancelIfEmpty !== false;
  if (!event?.dataTransfer || !photo) {
    if (cancelIfEmpty) event?.preventDefault?.();
    return false;
  }
  const wanted = meetingPhotoDownloadName(photo);
  // El handle puede ser el icono Copiar: subir al thumb que tiene el <img>.
  const node =
    event.currentTarget?.closest?.("[data-meeting-photo-id]") ||
    event.currentTarget;
  const img =
    node?.tagName === "IMG" ? node : node?.querySelector?.("img") || null;
  const named = fileWithMeetingPhotoName(
    photo,
    file || cachedMeetingPhotoFile(photo) || fileFromLoadedImage(img, wanted),
  );
  const dataUrl =
    options.downloadUrl === false
      ? ""
      : cachedMeetingPhotoDataUrl(photo) || compactJpegDataUrlFromImage(img);
  const ok = attachNamedFileToDrag(event, named, dataUrl, {
    downloadUrl: options.downloadUrl !== false,
  });
  if (img && event.dataTransfer.setDragImage) {
    try {
      event.dataTransfer.setDragImage(
        img,
        Math.min(48, img.clientWidth / 3 || 24),
        Math.min(48, img.clientHeight / 3 || 24),
      );
    } catch {
      /* ignore */
    }
  }
  if (!ok && cancelIfEmpty) event.preventDefault();
  return ok;
}

export { fileWithMeetingPhotoName };
